import { useEffect, useState } from "react";
import { Minus, Plus, X } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "../../components/ui/drawer";
import FoodImage from '../../components/FoodImage';
import { VegMark } from "../../components/VegMark";
import { inr, uuid } from "../../lib/api";

export default function ItemSheet({ item, items = [], groups, onClose, onAdd }) {
  const [sel, setSel] = useState({});
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  useEffect(() => { setSel({}); setQty(1); setNotes(""); }, [item]);
  if (!item) return null;
  const myGroups = groups.filter((g) => (item.modifier_group_ids || []).includes(g.id));
  const toggle = (g, oid) => setSel((s) => {
    const cur = s[g.id] || [];
    if (g.max_select === 1) return { ...s, [g.id]: !g.required && !g.min_select && cur.includes(oid) ? [] : [oid] };
    if (cur.includes(oid)) return { ...s, [g.id]: cur.filter((x) => x !== oid) };
    if (cur.length >= g.max_select) return s;
    return { ...s, [g.id]: [...cur, oid] };
  });
  const invalid = myGroups.find((g) => (sel[g.id] || []).length < Math.max(g.min_select, g.required ? 1 : 0));
  const chosen = myGroups.flatMap((g) => g.options.filter((o) => (sel[g.id] || []).includes(o.id)));
  const unit = item.price_paise + chosen.reduce((a, o) => a + o.price_paise, 0);

  return (
    <Drawer open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="mx-auto max-h-[92vh] max-w-md bg-[#FAF6F0]" data-testid="item-sheet">
        <DrawerDescription className="sr-only">Choose options and quantity before adding this dish to your order.</DrawerDescription>
        <div className="flex shrink-0 justify-end border-b border-stone-200 px-3 py-1">
          <button type="button" onClick={onClose} aria-label="Close item details" data-testid="item-sheet-close" className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-semibold text-stone-700 hover:bg-stone-100">
            <X className="h-5 w-5" aria-hidden="true" /> Close
          </button>
        </div>
        <div className="overflow-y-auto px-5 pb-4">
          <FoodImage src={item.image_url} alt={item.name} className="mt-2 h-48 w-full rounded-2xl object-cover"/>
          <div className="mt-4 flex items-center gap-2"><VegMark flag={item.veg_flag} /><DrawerTitle className="text-2xl font-bold">{item.name}</DrawerTitle></div>
          <p className="mt-1 text-sm text-stone-600">{item.description}</p>
          {item.combo_item_ids?.length > 0 && <section className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="item-combo-details"><h3 className="text-sm font-bold text-amber-800">Combo includes</h3><ul className="mt-2 list-inside list-disc space-y-1 text-sm text-stone-700">{items.filter(i => item.combo_item_ids.includes(i.id)).map(i => <li key={i.id}>{i.name}</li>)}</ul><p className="mt-2 text-xs text-stone-500">Included in the combo price.</p></section>}
          {myGroups.map((g) => (
            <div key={g.id} className="mt-5" data-testid={`modifier-group-${g.id}`}>
              <div className="flex items-baseline justify-between">
                <h4 className="font-semibold">{g.name}</h4>
                <span className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
                  {g.required || g.min_select ? "Required" : "Optional"} · {g.max_select === 1 ? "pick 1" : `up to ${g.max_select}`}
                </span>
              </div>
              <div className="mt-2 divide-y divide-stone-200 rounded-xl border border-stone-200 bg-white">
                {g.options.map((o) => {
                  const on = (sel[g.id] || []).includes(o.id);
                  return (
                    <button key={o.id} data-testid={`modifier-option-${o.id}`} onClick={() => toggle(g, o.id)} className="flex w-full items-center justify-between px-4 py-3 text-sm">
                      <span className="flex items-center gap-3">
                        <span className={`grid h-5 w-5 place-items-center border-2 ${g.max_select === 1 ? "rounded-full" : "rounded-md"} ${on ? "border-[#FF9D05] bg-[#FF9D05]" : "border-stone-300"}`}>
                          {on && <span className="h-2 w-2 rounded-full bg-white" />}
                        </span>
                        {o.name}
                      </span>
                      <span className="text-stone-500">{o.price_paise ? `+${inr(o.price_paise)}` : "Free"}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <textarea data-testid="item-notes-input" value={notes} maxLength={200} onChange={(e) => setNotes(e.target.value)} placeholder="Cooking notes (optional)"
            className="mt-5 w-full rounded-xl border border-stone-200 bg-white p-3 text-sm outline-none focus:border-[#FF9D05]" rows={2} />
        </div>
        <div className="flex items-center gap-3 border-t border-stone-200 bg-white px-5 py-4">
          <div className="flex items-center rounded-xl border border-stone-300">
            <button data-testid="item-qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3"><Minus className="h-4 w-4" /></button>
            <span className="w-6 text-center font-bold" data-testid="item-qty-value">{qty}</span>
            <button data-testid="item-qty-increase" onClick={() => setQty((q) => Math.min(20, q + 1))} className="p-3"><Plus className="h-4 w-4" /></button>
          </div>
          <button data-testid="item-add-to-cart-button" disabled={!!invalid}
            onClick={() => onAdd({ key: uuid(), item_id: item.id, name: item.name, veg_flag: item.veg_flag, qty, unit, notes, modifier_ids: chosen.map((o) => o.id), mods: chosen.map((o) => o.name) })}
            className="flex-1 rounded-xl bg-[#FF9D05] py-3.5 font-semibold text-white transition-colors hover:bg-[#E08C00] disabled:bg-stone-300">
            {invalid ? `Choose ${invalid.name.toLowerCase()}` : `Add · ${inr(unit * qty)}`}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
